import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

const LOGO = process.env.PUBLIC_URL + "/nextrota-logo.png";

/**
 * Marketing-site header. Shared by every public page (Home, Features, Pricing,
 * About, Contact + the legal pages) so navigation stays in lock-step. Mobile
 * uses a Sheet drawer; desktop shows links inline.
 *
 * The "Start Free Trial" button is rendered in NextRota's primary brand colour
 * (`#2563EB`) so it always reads as the prominent call to action.
 */
const LINKS = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand-link">
          <img src={LOGO} alt="NextRota" className="h-8 sm:h-10 w-auto"/>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"}
              data-testid={`public-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "text-[#1D4ED8] bg-[#DBE7FE]" : "text-[#4B5773] hover:text-[#0B1A36]"
                }`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="public-login-btn"
            className="hidden sm:inline-flex px-3.5 py-1.5 rounded-md text-sm font-medium text-[#1F2A44] hover:bg-slate-100">
            Login
          </Link>
          <Link to="/start" data-testid="public-start-trial-btn"
            className="inline-flex px-4 py-2 rounded-md text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] shadow-sm">
            Start Free Trial
          </Link>
          <button type="button" onClick={()=>setOpen(true)}
            data-testid="public-menu-btn"
            className="md:hidden p-2 rounded-md text-[#1F2A44] hover:bg-slate-100"
            aria-label="Open navigation">
            <Menu className="h-5 w-5"/>
          </button>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-72 p-0" data-testid="public-nav-drawer">
          <SheetHeader className="px-5 py-4 border-b border-slate-100 text-left">
            <SheetTitle className="font-display text-lg">NextRota</SheetTitle>
          </SheetHeader>
          <nav className="p-2 flex flex-col">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"}
                onClick={()=>setOpen(false)}
                data-testid={`public-drawer-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium ${
                    isActive ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#1F2A44] hover:bg-slate-100"
                  }`}>{l.label}</NavLink>
            ))}
            <Link to="/login" onClick={()=>setOpen(false)}
              className="px-4 py-3 rounded-lg text-base text-[#1F2A44] hover:bg-slate-100">
              Login
            </Link>
            <Link to="/start" onClick={()=>setOpen(false)}
              className="m-2 px-4 py-3 rounded-lg text-base font-semibold bg-[#2563EB] text-white text-center">
              Start Free Trial
            </Link>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
